
import { useState, useEffect, useRef } from 'react';
import { useStripeStatus } from '@/hooks/useStripeStatus';
import { fetchStripeAccountId } from '@/services/transactions/stripeAccountService';

/**
 * Hook to track the Stripe connection status using both cache and direct DB checks
 */
export function useConnectionStatus() {
  const { 
    isConnected, 
    accountId, 
    checkConnection, 
    isLoading, 
    error,
    clearCache 
  } = useStripeStatus();
  
  const [configError, setConfigError] = useState<string | null>(null);
  const [localAccountId, setLocalAccountId] = useState<string | null>(null);
  const [localCheckDone, setLocalCheckDone] = useState<boolean>(false);
  const hasPerformedLocalCheckRef = useRef(false);
  const checkConnectionRef = useRef(checkConnection);
  
  // Keep ref updated
  useEffect(() => {
    checkConnectionRef.current = checkConnection;
  }, [checkConnection]);
  
  // Add an additional direct check against the database for Stripe account ID
  // Only run once per component instance, not every time isConnected changes
  useEffect(() => {
    // Skip if already connected or already performed the check
    // This prevents multiple components from all performing the same check
    if (isConnected || hasPerformedLocalCheckRef.current) return;

    let isMounted = true;
    hasPerformedLocalCheckRef.current = true;

    const performLocalCheck = async () => {
      try {
        const fetchedAccountId = await fetchStripeAccountId();
        
        if (isMounted && fetchedAccountId) {
          setLocalAccountId(fetchedAccountId);
          // Force refresh connection status if we find a valid account
          await checkConnectionRef.current(true);
        }
      } catch (err) {
        console.error('[Stripe Debug] Error in local Stripe account check:', err);
      } finally {
        if (isMounted) {
          setLocalCheckDone(true);
        }
      }
    };
    
    performLocalCheck();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount, not on every isConnected change
  
  // Update error state when the main hook's error changes
  useEffect(() => {
    if (error) {
      console.error('[Stripe Debug] Stripe configuration error:', error);
      setConfigError(error);
    } else {
      setConfigError(null);
    }
  }, [error]);
  
  // Determine the actual connection status using both sources of truth
  const actuallyConnected = isConnected || !!localAccountId;
  const actualAccountId = accountId || localAccountId;
  
  // Check for discrepancies between the two data sources
  useEffect(() => {
    if (isConnected !== !!localAccountId && !isLoading && localCheckDone) {
      console.warn('[Stripe Debug] Connection status discrepancy:', {
        statusHook: isConnected ? 'Connected' : 'Not connected',
        directCheck: localAccountId ? 'Connected' : 'Not connected',
        statusHookAccountId: accountId,
        directCheckAccountId: localAccountId
      });
      
      // Force refresh if we detect a discrepancy
      checkConnectionRef.current(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, localAccountId, isLoading, localCheckDone, accountId]); // Removed checkConnection from dependencies

  return {
    isConnected: actuallyConnected,
    accountId: actualAccountId,
    isLoading: isLoading && !localCheckDone,
    configError,
    checkConnection,
    clearCache,
    localCheckDone
  };
}
